import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  TextField,
  CircularProgress,
  Box,
  Stack,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import {
  FaArrowLeft,
  FaEdit,
  FaTrash,
  FaImage,
  FaListAlt,
  FaPlus,
} from "react-icons/fa";
import { MathfieldElement } from "mathlive";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";
import "mathlive";
import "../css/mathlive.core.css";

import { API_URL } from "../config";

// Register math-field
if (!customElements.get("math-field")) {
  customElements.define("math-field", MathfieldElement);
}

export default function AdminTestQuestionPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const imageInputRef = useRef(null);

  const [mode, setMode] = useState("text");
  const [questions, setQuestions] = useState([]);
  const [number, setNumber] = useState("");
  const [text, setText] = useState("");
  const [solution, setSolution] = useState("");
  const [imageUri, setImageUri] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);

  const fetchQuestions = useCallback(() => {
    fetch(`${API_URL}/admin/questions/${testId}/`)
      .then((res) => res.json())
      .then((data) => setQuestions(data))
      .catch(() => alert("Error fetching questions."));
  }, [testId]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const removeBackgroundWithRemoveBg = async (file) => {
    const formData = new FormData();
    formData.append("image_file", file);
    formData.append("size", "auto");

    const res = await fetch("https://api.remove.bg/v1.0/removebg", {
      method: "POST",
      headers: { "X-Api-Key": "hxRA6YNfZzrnsXgMYv9ZBYCh" },
      body: formData,
    });

    if (!res.ok) throw new Error("Background removal failed");
    return await res.blob();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsRemovingBg(true);
    try {
      const blob = await removeBackgroundWithRemoveBg(file);
      const cleanedFile = new File([blob], "cleaned.png", {
        type: "image/png",
      });
      setImageUri(cleanedFile);
      setImagePreview(URL.createObjectURL(cleanedFile));
      setExistingImage(null);
    } catch {
      alert("Background removal failed – using original image.");
      setImageUri(file);
      setImagePreview(URL.createObjectURL(file));
      setExistingImage(null);
    } finally {
      setIsRemovingBg(false);
    }
  };

  const resetForm = () => {
    setNumber("");
    setText("");
    setSolution("");
    setImageUri(null);
    setImagePreview(null);
    setExistingImage(null);
    setEditingQuestionId(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const submitQuestion = () => {
    if (!number.trim() || !text.trim()) {
      alert("Question number and text are required.");
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append("number", number);
    formData.append("text", text);
    formData.append("solution", solution || "");
    if (imageUri) formData.append("q_img", imageUri);

    const url = editingQuestionId
      ? `${API_URL}/admin/question/${editingQuestionId}/`
      : `${API_URL}/admin/questions/${testId}/`;

    fetch(url, {
      method: editingQuestionId ? "PUT" : "POST",
      body: formData,
    })
      .then(() => {
        fetchQuestions();
        resetForm();
      })
      .catch(() => alert("Error submitting question."))
      .finally(() => setIsLoading(false));
  };

  const startEditing = (q) => {
    setEditingQuestionId(q.id);
    setNumber(q.number.toString());
    setText(q.text || "");
    setSolution(q.solution || "");
    setExistingImage(q.q_img || null);
    setImageUri(null);
    setImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteQuestion = (id) => {
    if (
      !window.confirm("Delete this question and all its options permanently?")
    )
      return;

    fetch(`${API_URL}/admin/question/${id}/`, { method: "DELETE" })
      .then(() => setQuestions((prev) => prev.filter((q) => q.id !== id)))
      .catch(() => alert("Failed to delete question."));
  };

  const renderLatex = (text) => {
    if (!text) return null;

    const parts = text.split(
      /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g
    );

    return parts.map((part, index) => {
      // $$...$$ (block)
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \[...\] (block)
      if (part.startsWith("\\[") && part.endsWith("\\]")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \(...\) (inline)
      if (part.startsWith("\\(") && part.endsWith("\\)")) {
        return (
          <InlineMath key={index} math={part.slice(2, -2)} />
        );
      }

      // $...$ (inline)
      if (part.startsWith("$") && part.endsWith("$")) {
        return (
          <InlineMath key={index} math={part.slice(1, -1)} />
        );
      }

      // Normal text
      return (
        <span key={index} style={{ whiteSpace: "pre-line" }}>
          {part}
        </span>
      );
    });
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", p: { xs: 2, sm: 3 } }}>
      {/* Back Button */}
      <IconButton onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        <FaArrowLeft />
      </IconButton>

      <Typography variant="h4" align="center" fontWeight="600" gutterBottom>
        {editingQuestionId ? "Edit Question" : "Manage Questions"}
      </Typography>

      {/* Form Card */}
      <Card elevation={4} sx={{ mb: 5 }}>
        <CardContent sx={{ pb: 1 }}>
          <Stack spacing={3}>
            {/* Question Number */}
            <TextField
              label="Question Number"
              type="number"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 1 }}
            />

            {/* Question Text */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              {mode === "text" ? (
                <TextField
                  label="Question Text"
                  multiline
                  rows={5}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Use $...$ for inline math, $$...$$ for display math"
                  fullWidth
                  variant="outlined"
                />
              ) : (
                <math-field
                  style={{
                    flex: 1,
                    minHeight: 160,
                    padding: "16px",
                    border: "1px solid #ccc",
                    borderRadius: 8,
                    background: "#fff",
                    fontSize: "18px",
                  }}
                  value={text}
                  onInput={(e) => setText(e.target.getValue())}
                />
              )}

              <Stack spacing={1}>
                <Button
                  variant={mode === "text" ? "contained" : "outlined"}
                  onClick={() => setMode("text")}
                >
                  Text
                </Button>
                <Button
                  variant={mode === "math" ? "contained" : "outlined"}
                  onClick={() => setMode("math")}
                >
                  Math
                </Button>
              </Stack>
            </Box>

            {/* Solution */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              {mode === "text" ? (
                <TextField
                  label="Solution / Explanation (optional)"
                  multiline
                  rows={4}
                  value={solution}
                  onChange={(e) => setSolution(e.target.value)}
                  placeholder="Use $...$ or $$...$$ for math"
                  fullWidth
                  variant="outlined"
                />
              ) : (
                <math-field
                  style={{
                    flex: 1,
                    minHeight: 140,
                    padding: "16px",
                    border: "1px solid #ccc",
                    borderRadius: 8,
                    background: "#fff",
                    fontSize: "18px",
                  }}
                  value={solution}
                  onInput={(e) => setSolution(e.target.getValue())}
                />
              )}
            </Box>

            {/* Image Upload */}
            <Box>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="question-image-upload"
                type="file"
                ref={imageInputRef}
                onChange={handleImageUpload}
              />
              <label htmlFor="question-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<FaImage />}
                >
                  Upload Question Image
                </Button>
              </label>

              {isRemovingBg && (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Removing background...
                  </Typography>
                </Box>
              )}

              {(imagePreview || existingImage) && (
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <img
                    src={imagePreview || existingImage}
                    alt="Question preview"
                    style={{
                      maxHeight: 350,
                      maxWidth: "100%",
                      borderRadius: 12,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    }}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </CardContent>

        <CardActions sx={{ justifyContent: "flex-end", p: 2, gap: 1 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={editingQuestionId ? <FaEdit /> : <FaPlus />}
            onClick={submitQuestion}
            disabled={isLoading || isRemovingBg}
          >
            {isLoading
              ? "Saving..."
              : editingQuestionId
              ? "Update Question"
              : "Add Question"}
          </Button>
          {editingQuestionId && (
            <Button variant="outlined" onClick={resetForm}>
              Cancel
            </Button>
          )}
        </CardActions>
      </Card>

      <Divider sx={{ my: 5 }}>
        <Chip label={`All Questions (${questions.length})`} />
      </Divider>

      {/* Questions List */}
      {questions.length === 0 ? (
        <Alert severity="info">No questions added yet.</Alert>
      ) : (
        <Stack spacing={3}>
          {questions.map((q) => (
            <Card key={q.id} variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      Question {q.number}
                    </Typography>

                    <Box sx={{ mt: 2 }}>{renderLatex(q.text)}</Box>

                    {q.solution && (
                      <Box
                        sx={{
                          mt: 3,
                          p: 2,
                          bgcolor: "grey.50",
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Solution:
                        </Typography>
                        {renderLatex(q.solution)}
                      </Box>
                    )}

                    {q.q_img && (
                      <Box sx={{ mt: 3, textAlign: "center" }}>
                        <img
                          src={q.q_img}
                          alt="Question"
                          style={{ maxHeight: 200, borderRadius: 8 }}
                        />
                      </Box>
                    )}
                  </Box>

                  <Stack spacing={1} sx={{ ml: 2 }}>
                    <Tooltip title="Manage Options">
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/admin-test-options/${q.id}`)}
                      >
                        <FaListAlt />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton color="info" onClick={() => startEditing(q)}>
                        <FaEdit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        onClick={() => deleteQuestion(q.id)}
                      >
                        <FaTrash />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
